import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useAuth } from "@/lib/auth";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Save, Send, ArrowLeft, BookOpen, FileText, Eye, CheckCircle2, AlertCircle, Clock, ChevronUp, ChevronDown, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { BlockTypeSelector } from "@/components/admin/help/BlockTypeSelector";
import { ParagraphBlockEditor } from "@/components/admin/help/ParagraphBlockEditor";
import { HeadingBlockEditor } from "@/components/admin/help/HeadingBlockEditor";
import { CalloutBlockEditor } from "@/components/admin/help/CalloutBlockEditor";
import { ImageBlockEditor } from "@/components/admin/help/ImageBlockEditor";
import { VideoBlockEditor } from "@/components/admin/help/VideoBlockEditor";
import { ArticlePreviewModal } from "@/components/admin/help/ArticlePreviewModal";
import { ArticleEditorErrorBoundary } from "@/components/admin/help/ArticleEditorErrorBoundary";
import { createDefaultContentBlocks } from "@/lib/helpArticleEditorTemplate";

import type {
  ContentBlock,
  ContentBlockType,
  HelpArticleContent,
  ParagraphBlock,
  HeadingBlock,
  CalloutBlock,
  ImageBlock,
  VideoBlock,
} from "@/types/helpArticle";
import { createContentBlock } from "@/types/helpArticle";
import type { HelpCategory } from "@/hooks/useHelpCenter";

// Sortable wrapper for content blocks with mobile reorder buttons
function SortableBlockEditor({
  block,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}: {
  block: ContentBlock;
  onChange: (block: ContentBlock) => void;
  onDelete: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  // Create dragHandleProps to pass to block editors
  const dragHandleProps = {
    attributes,
    listeners,
  };

  // Mobile reorder props
  const mobileReorderProps = {
    onMoveUp,
    onMoveDown,
    isFirst,
    isLast,
  };

  // Render the appropriate block editor with drag handle props
  const renderEditor = () => {
    const commonProps = { onDelete, dragHandleProps, mobileReorderProps };

    switch (block.type) {
      case "paragraph":
        return (
          <ParagraphBlockEditor
            block={block as ParagraphBlock}
            onChange={(b) => onChange(b)}
            {...commonProps}
          />
        );
      case "heading":
        return (
          <HeadingBlockEditor
            block={block as HeadingBlock}
            onChange={(b) => onChange(b)}
            {...commonProps}
          />
        );
      case "callout":
        return (
          <CalloutBlockEditor
            block={block as CalloutBlock}
            onChange={(b) => onChange(b)}
            {...commonProps}
          />
        );
      case "image":
        return (
          <ImageBlockEditor
            block={block as ImageBlock}
            onChange={(b) => onChange(b)}
            {...commonProps}
          />
        );
      case "video":
        return (
          <VideoBlockEditor
            block={block as VideoBlock}
            onChange={(b) => onChange(b)}
            {...commonProps}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={isDragging ? "shadow-lg rounded-lg" : ""}
    >
      {renderEditor()}
    </div>
  );
}

type HelpVisibility = Database["public"]["Enums"]["help_visibility"];

const VISIBILITY_OPTIONS: { value: HelpVisibility; label: string; hint: string }[] = [
  { value: "all", label: "Everyone", hint: "Visible to all signed-in users" },
  { value: "mentor", label: "Mentors only", hint: "Only users with the mentor role" },
  { value: "entrepreneur", label: "Entrepreneurs only", hint: "Only entrepreneur users" },
  { value: "hidden", label: "Hidden", hint: "Not listed for anyone (admins only)" },
];

function ArticleEditorContent() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const isEditMode = !!id;

  // Form state
  const [title, setTitle] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [contentBlocks, setContentBlocks] = useState<ContentBlock[]>([]);
  // Published state and visibility are tracked separately and hydrated from the
  // saved article. They used to be hardcoded in the save payload, so every
  // auto-save silently unpublished the article and reset its visibility to "all".
  const [isPublished, setIsPublished] = useState(false);
  const [visibility, setVisibility] = useState<HelpVisibility>("all");
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  // Track initial load for edit mode
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasInitializedNewArticle, setHasInitializedNewArticle] = useState(false);

  // Auto-save ref
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Navigation confirmation state for unsaved changes
  const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Validation errors
  const [errors, setErrors] = useState<{
    title?: string;
    categoryId?: string;
    contentBlocks?: string;
  }>({});

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Check if user is admin
  const { data: isAdmin, isLoading: adminLoading } = useQuery({
    queryKey: ["is_admin", user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const { data } = await supabase.rpc("has_role", { _user_id: user.id, _role: "admin" });
      return data;
    },
    enabled: !!user?.id,
  });

  // Fetch categories
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ["help-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("help_categories")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });

      if (error) throw error;
      return data as HelpCategory[];
    },
    enabled: isAdmin === true,
  });

  // Fetch article for edit mode
  const { data: existingArticle, isLoading: articleLoading, error: articleError } = useQuery({
    queryKey: ["help-article", id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("help_articles")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          // Article not found
          return null;
        }
        throw error;
      }
      return data;
    },
    enabled: isEditMode && isAdmin === true,
    retry: false,
  });

  // Initialize default content blocks for new articles
  useEffect(() => {
    if (!isEditMode && !hasInitializedNewArticle && isAdmin === true) {
      const defaultBlocks = createDefaultContentBlocks();
      setContentBlocks(defaultBlocks as ContentBlock[]);
      setHasInitializedNewArticle(true);
      setIsInitialLoad(false);
    }
  }, [isEditMode, hasInitializedNewArticle, isAdmin]);

  // Populate form when editing
  useEffect(() => {
    if (existingArticle && isInitialLoad) {
      try {
        setTitle(existingArticle.title || "");
        setCategoryId(existingArticle.category_id || "");
        setSubtitle(existingArticle.summary || "");
        setIsPublished(existingArticle.is_published === true);
        setVisibility((existingArticle.visibility as HelpVisibility) ?? "all");

        // Parse body as JSON content blocks
        if (existingArticle.body) {
          try {
            const content = JSON.parse(existingArticle.body) as HelpArticleContent;
            if (content?.blocks && Array.isArray(content.blocks)) {
              setContentBlocks(content.blocks);
            } else {
              // Content is JSON but not the expected format
              setContentBlocks([
                {
                  id: crypto.randomUUID(),
                  type: "paragraph",
                  content: existingArticle.body,
                } as ParagraphBlock,
              ]);
            }
          } catch {
            // Body isn't valid JSON, treat it as a single paragraph (legacy HTML)
            setContentBlocks([
              {
                id: crypto.randomUUID(),
                type: "paragraph",
                content: existingArticle.body,
              } as ParagraphBlock,
            ]);
          }
        } else {
          setContentBlocks([]);
        }

        setIsInitialLoad(false);
      } catch (error) {
        console.error("Error parsing article data:", error);
        toast.error("Error loading article content");
        setIsInitialLoad(false);
      }
    }
  }, [existingArticle, isInitialLoad]);

  // Track changes
  useEffect(() => {
    if (!isInitialLoad) {
      setHasUnsavedChanges(true);
      setSaveStatus("idle");
    }
  }, [title, categoryId, subtitle, contentBlocks, visibility, isInitialLoad]);

  // Auto-save with 3-second debounce
  useEffect(() => {
    if (hasUnsavedChanges && title && categoryId && !isInitialLoad) {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      autoSaveTimeoutRef.current = setTimeout(() => {
        handleSave(null, true); // Silent auto-save; never changes is_published
      }, 3000);
    }

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [hasUnsavedChanges, title, categoryId, subtitle, contentBlocks, visibility, isInitialLoad]);

  // Browser beforeunload protection
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Safe navigation helper that confirms before leaving with unsaved changes
  const safeNavigate = useCallback((to: string) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(to);
      setShowLeaveConfirmation(true);
    } else {
      navigate(to);
    }
  }, [hasUnsavedChanges, navigate]);

  // Redirect if not authenticated or not admin
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!adminLoading && isAdmin === false) {
      toast.error("You don't have permission to access this page");
      navigate("/dashboard");
    }
  }, [isAdmin, adminLoading, navigate]);

  // Validation
  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    if (!title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!categoryId) {
      newErrors.categoryId = "Category is required";
    }

    if (contentBlocks.length === 0) {
      newErrors.contentBlocks = "At least one content block is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save article
  const handleSave = useCallback(
    // publish === null  -> leave is_published untouched (auto-save / draft save)
    // publish === true  -> publish now
    // publish === false -> explicitly unpublish
    async (publish: boolean | null = null, silent: boolean = false) => {
      if (!validate() && !silent) {
        toast.error("Please fix the validation errors");
        return;
      }

      // Clear any pending auto-save
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      if (publish === true) {
        setIsPublishing(true);
      } else {
        setIsSaving(true);
      }
      setSaveStatus("saving");

      try {
        const articleContent: HelpArticleContent = { blocks: contentBlocks };
        const bodyJson = JSON.stringify(articleContent);

        // is_published is only written when the user explicitly clicks
        // Publish/Unpublish. Auto-save and "Save Draft" must NOT touch it —
        // sending is_published: false on every auto-save is what silently
        // unpublished live articles ~3 seconds after an admin started typing.
        const articleData = {
          title,
          category_id: categoryId,
          summary: subtitle || null,
          body: bodyJson,
          visibility,
          ...(publish === null ? {} : { is_published: publish }),
        };

        if (isEditMode && id) {
          // Update existing article
          const { error } = await supabase
            .from("help_articles")
            .update({
              ...articleData,
              updated_by: user?.id,
            })
            .eq("id", id);

          if (error) throw error;

          if (!silent) {
            toast.success(
              publish === true
                ? "Article published!"
                : publish === false
                  ? "Article unpublished"
                  : "Draft saved"
            );
          }
        } else {
          // Create new article
          // Every new article used to get sort_order: 0, so manual ordering did
          // nothing. Append to the end of its category instead.
          const { data: lastInCategory } = await supabase
            .from("help_articles")
            .select("sort_order")
            .eq("category_id", categoryId)
            .order("sort_order", { ascending: false })
            .limit(1)
            .maybeSingle();

          const nextSortOrder = (lastInCategory?.sort_order ?? -1) + 1;

          const { data, error } = await supabase
            .from("help_articles")
            .insert({
              ...articleData,
              // New articles need an explicit value; default to unpublished.
              is_published: publish === true,
              created_by: user?.id,
              updated_by: user?.id,
              sort_order: nextSortOrder,
            })
            .select()
            .single();

          if (error) throw error;

          if (!silent) {
            toast.success(publish === true ? "Article published!" : "Draft saved");
          }

          // Navigate to edit mode for the new article
          if (data) {
            navigate(`/admin/help/articles/${data.id}/edit`, { replace: true });
          }
        }

        if (publish !== null) {
          setIsPublished(publish);
        }
        setHasUnsavedChanges(false);
        setLastSavedAt(new Date());
        setSaveStatus("saved");
        queryClient.invalidateQueries({ queryKey: ["admin-help-articles"] });
        queryClient.invalidateQueries({ queryKey: ["help-articles"] });
      } catch (error) {
        console.error("Save error:", error);
        setSaveStatus("error");
        if (!silent) {
          toast.error("Failed to save article");
        }
      } finally {
        setIsSaving(false);
        setIsPublishing(false);
      }
    },
    [title, categoryId, subtitle, contentBlocks, visibility, isEditMode, id, user, navigate, queryClient]
  );

  // Handle block type selection
  const handleAddBlock = (type: ContentBlockType) => {
    const newBlock = createContentBlock(type);
    setContentBlocks([...contentBlocks, newBlock]);
  };

  // Handle block change
  const handleBlockChange = (index: number, updatedBlock: ContentBlock) => {
    const newBlocks = [...contentBlocks];
    newBlocks[index] = updatedBlock;
    setContentBlocks(newBlocks);
  };

  // Handle block delete
  const handleBlockDelete = (index: number) => {
    const newBlocks = contentBlocks.filter((_, i) => i !== index);
    setContentBlocks(newBlocks);
  };

  // Handle block move (for mobile reordering)
  const handleBlockMoveUp = (index: number) => {
    if (index === 0) return;
    setContentBlocks((items) => arrayMove(items, index, index - 1));
  };

  const handleBlockMoveDown = (index: number) => {
    if (index === contentBlocks.length - 1) return;
    setContentBlocks((items) => arrayMove(items, index, index + 1));
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setContentBlocks((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Loading states
  if (authLoading || adminLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  if (isEditMode && articleLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto" />
            <p className="text-muted-foreground mt-2">Loading article...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Article not found
  if (isEditMode && !articleLoading && !existingArticle) {
    return (
      <DashboardLayout>
        <div className="container py-6 max-w-4xl">
          <Card className="border-destructive/50">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-destructive/10">
                  <AlertTriangle className="h-6 w-6 text-destructive" />
                </div>
                <div>
                  <CardTitle className="text-lg text-destructive">
                    Article Not Found
                  </CardTitle>
                  <CardDescription>
                    The article you're looking for doesn't exist or may have been deleted.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => navigate("/admin/help/articles")}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Articles
                </Button>
                <Button onClick={() => navigate("/admin/help/articles/new")}>
                  Create New Article
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Article load error
  if (isEditMode && articleError) {
    return (
      <DashboardLayout>
        <div className="container py-6 max-w-4xl">
          <Card className="border-destructive/50">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-destructive/10">
                  <AlertCircle className="h-6 w-6 text-destructive" />
                </div>
                <div>
                  <CardTitle className="text-lg text-destructive">
                    Error Loading Article
                  </CardTitle>
                  <CardDescription>
                    Something went wrong while loading this article.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted rounded-lg p-4">
                <p className="text-sm text-muted-foreground font-mono">
                  {articleError instanceof Error ? articleError.message : "An unexpected error occurred"}
                </p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => navigate("/admin/help/articles")}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Articles
                </Button>
                <Button onClick={() => window.location.reload()}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container py-6 max-w-4xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => safeNavigate("/admin/help/articles")}
              className="shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <BookOpen className="h-6 w-6 text-primary" />
                {isEditMode ? "Edit Article" : "New Article"}
              </h1>
              <div className="flex items-center gap-2 text-xs mt-1">
                {saveStatus === "saving" && (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                    <span className="text-muted-foreground">Saving...</span>
                  </>
                )}
                {saveStatus === "saved" && !hasUnsavedChanges && lastSavedAt && (
                  <>
                    <CheckCircle2 className="h-3 w-3 text-green-600" />
                    <span className="text-green-600">
                      All changes saved at {lastSavedAt.toLocaleTimeString()}
                    </span>
                  </>
                )}
                {hasUnsavedChanges && saveStatus !== "saving" && (
                  <>
                    <Clock className="h-3 w-3 text-amber-500" />
                    <span className="text-amber-500">Unsaved changes</span>
                  </>
                )}
                {saveStatus === "error" && (
                  <>
                    <AlertCircle className="h-3 w-3 text-destructive" />
                    <span className="text-destructive">Save failed</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setIsPreviewOpen(true)}
              disabled={isSaving || isPublishing}
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSave(null)}
              disabled={isSaving || isPublishing}
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save
            </Button>
            {isPublished && (
              <Button
                variant="outline"
                onClick={() => handleSave(false)}
                disabled={isSaving || isPublishing}
              >
                Unpublish
              </Button>
            )}
            <Button
              onClick={() => handleSave(true)}
              disabled={isSaving || isPublishing || isPublished}
            >
              {isPublishing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {isPublished ? "Published" : "Publish"}
            </Button>
          </div>
        </div>

        {/* Basic Info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Basic Information</CardTitle>
            <CardDescription>Set the article title, category, and summary</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter article title..."
                className={errors.title ? "border-destructive" : ""}
              />
              {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category">
                Category <span className="text-destructive">*</span>
              </Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger className={errors.categoryId ? "border-destructive" : ""}>
                  <SelectValue placeholder="Select a category..." />
                </SelectTrigger>
                <SelectContent className="bg-popover z-50">
                  {categoriesLoading ? (
                    <div className="p-2 text-center text-muted-foreground">Loading...</div>
                  ) : categories?.length === 0 ? (
                    <div className="p-2 text-center text-muted-foreground">No categories found</div>
                  ) : (
                    categories?.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {errors.categoryId && <p className="text-sm text-destructive">{errors.categoryId}</p>}
            </div>

            {/* Visibility — previously hardcoded to "all" in the save payload,
                which reset mentor-only/entrepreneur-only/hidden articles to
                public on every edit and auto-save. */}
            <div className="space-y-2">
              <Label htmlFor="visibility">Who can see this article</Label>
              <Select
                value={visibility}
                onValueChange={(v) => setVisibility(v as HelpVisibility)}
              >
                <SelectTrigger id="visibility">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover z-50">
                  {VISIBILITY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {VISIBILITY_OPTIONS.find((o) => o.value === visibility)?.hint}
              </p>
            </div>

            {/* Subtitle */}
            <div className="space-y-2">
              <Label htmlFor="subtitle">Subtitle (optional)</Label>
              <Textarea
                id="subtitle"
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
                placeholder="Brief description of the article..."
                className="min-h-[80px]"
              />
            </div>
          </CardContent>
        </Card>

        {/* Content Blocks */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Content</CardTitle>
            <CardDescription>
              Build your article using content blocks. Drag to reorder or use the arrow buttons on mobile.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {errors.contentBlocks && (
              <p className="text-sm text-destructive">{errors.contentBlocks}</p>
            )}

            {contentBlocks.length === 0 ? (
              <div className="border-2 border-dashed rounded-lg p-8 text-center bg-muted/20">
                <FileText className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground mb-4">
                  No content blocks yet. Add your first block to get started.
                </p>
                <BlockTypeSelector onSelectBlockType={handleAddBlock} />
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={contentBlocks.map((b) => b.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2 sm:space-y-3">
                    {contentBlocks.map((block, index) => (
                      <SortableBlockEditor
                        key={block.id}
                        block={block}
                        onChange={(updatedBlock) => handleBlockChange(index, updatedBlock)}
                        onDelete={() => handleBlockDelete(index)}
                        onMoveUp={() => handleBlockMoveUp(index)}
                        onMoveDown={() => handleBlockMoveDown(index)}
                        isFirst={index === 0}
                        isLast={index === contentBlocks.length - 1}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}

            {contentBlocks.length > 0 && (
              <div className="pt-2">
                <BlockTypeSelector onSelectBlockType={handleAddBlock} />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Formatting Guide */}
        <Card className="mb-6 border-dashed bg-muted/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-primary" />
              Quick Formatting Guide
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              You can use basic HTML in paragraph blocks for rich formatting:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-2 p-2 bg-background rounded border">
                <code className="font-mono text-primary">&lt;strong&gt;text&lt;/strong&gt;</code>
                <span className="text-muted-foreground">→</span>
                <strong>Bold text</strong>
              </div>
              <div className="flex items-center gap-2 p-2 bg-background rounded border">
                <code className="font-mono text-primary">&lt;em&gt;text&lt;/em&gt;</code>
                <span className="text-muted-foreground">→</span>
                <em>Italic text</em>
              </div>
              <div className="flex items-center gap-2 p-2 bg-background rounded border">
                <code className="font-mono text-primary">&lt;ul&gt;&lt;li&gt;Item&lt;/li&gt;&lt;/ul&gt;</code>
                <span className="text-muted-foreground">→</span>
                <span>• Bullet list</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-background rounded border">
                <code className="font-mono text-primary">&lt;a href="URL"&gt;text&lt;/a&gt;</code>
                <span className="text-muted-foreground">→</span>
                <span className="text-primary underline">Link</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              <strong>Tip:</strong> Use the different block types (Heading, Callout, Image, Video) for a well-structured article.
            </p>
          </CardContent>
        </Card>

        {/* Bottom Actions */}
        <div className="flex justify-between items-center">
          <Button variant="outline" onClick={() => safeNavigate("/admin/help/articles")}>
            Cancel
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleSave(null)}
              disabled={isSaving || isPublishing}
            >
              {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
            <Button
              onClick={() => handleSave(true)}
              disabled={isSaving || isPublishing || isPublished}
            >
              {isPublishing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              {isPublished ? "Published" : "Publish"}
            </Button>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <ArticlePreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        article={{
          title,
          category: categories?.find((c) => c.id === categoryId)?.name || "",
          subtitle,
          content: { blocks: contentBlocks },
        }}
      />

      {/* Navigation Confirmation Dialog */}
      <AlertDialog open={showLeaveConfirmation} onOpenChange={setShowLeaveConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Do you want to leave without saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowLeaveConfirmation(false);
              setPendingNavigation(null);
            }}>
              Stay on page
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowLeaveConfirmation(false);
                if (pendingNavigation) {
                  navigate(pendingNavigation);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Leave anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}

// Default export wraps the editor with an error boundary
export default function ArticleEditor() {
  return (
    <ArticleEditorErrorBoundary>
      <ArticleEditorContent />
    </ArticleEditorErrorBoundary>
  );
}
